import type { Resource } from "../types";
import { RESOURCES, resourceMeta } from "../gameMeta";

export function ResourceIcon({ resource, className = "" }: { resource: Resource; className?: string }) {
  return (
    <svg
      className={`resource-icon resource-icon-${resource} ${className}`}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      data-resource-icon={resource}
    >
      {resourceShape(resource)}
    </svg>
  );
}

export function ResourceBadge({
  resource,
  amount,
  compact = false,
  quantityPrefix = "×",
  className = "",
  ariaLabel,
}: {
  resource: Resource;
  amount: number;
  compact?: boolean;
  quantityPrefix?: "×" | "−" | "+";
  className?: string;
  ariaLabel?: string;
}) {
  const meta = resourceMeta[resource];
  const accessible = ariaLabel || (quantityPrefix === "−"
    ? `${amount} ${meta.label} korting`
    : quantityPrefix === "+"
      ? `${amount} ${meta.label} erbij`
      : `${amount} ${meta.label}`);
  return (
    <span
      className={`resource-badge resource-badge-${resource} ${compact ? "compact" : ""} ${className}`}
      style={{ ["--resource-color" as string]: meta.color }}
      aria-label={accessible}
      data-resource={resource}
      data-amount={amount}
    >
      <ResourceIcon resource={resource} />
      <span className="resource-badge-name" aria-hidden="true">{compact ? meta.shortLabel : meta.label}</span>
      <strong aria-hidden="true">{quantityPrefix}{amount}</strong>
    </span>
  );
}

export function ResourceAmounts({ resources, compact = false, className = "" }: {
  resources: Resource[];
  compact?: boolean;
  className?: string;
}) {
  return (
    <span className={`resource-amounts ${className}`}>
      {RESOURCES.flatMap((resource) => {
        const amount = resources.filter((candidate) => candidate === resource).length;
        return amount ? [<ResourceBadge key={resource} resource={resource} amount={amount} compact={compact} />] : [];
      })}
    </span>
  );
}

export function ResourceCountSelector({
  selected,
  count,
  allowed = RESOURCES,
  disabled = false,
  compact = false,
  className = "",
  clearLabel = "Selectie wissen",
  canAdd,
  onAdd,
  onRemove,
  onClear,
}: {
  selected: Resource[];
  count: number;
  allowed?: Resource[];
  disabled?: boolean;
  compact?: boolean;
  className?: string;
  clearLabel?: string;
  canAdd?: (resource: Resource) => boolean;
  onAdd: (resource: Resource) => void;
  onRemove: (resource: Resource) => void;
  onClear: () => void;
}) {
  return (
    <div className={`resource-count-selector ${className}`}>
      {allowed.map((resource) => {
        const meta = resourceMeta[resource];
        const amount = selected.filter((item) => item === resource).length;
        const addAllowed = !disabled && (canAdd ? canAdd(resource) : selected.length < count);
        const removeAllowed = !disabled && amount > 0;
        return (
          <div className="resource-count-control" key={resource}>
            <button
              type="button"
              className="resource-count-step"
              disabled={!removeAllowed}
              aria-label={`Eén minder ${meta.label}`}
              onClick={() => onRemove(resource)}
            >−</button>
            <button
              type="button"
              className="resource-count-main"
              disabled={disabled}
              aria-disabled={!addAllowed && !removeAllowed}
              aria-label={`${meta.label}: ${amount} van ${count} gekozen`}
              aria-pressed={amount > 0}
              title="Klik of + om toe te voegen; gebruik − of de rechtermuisknop om te verwijderen"
              onClick={() => {
                if (addAllowed) onAdd(resource);
                else if (removeAllowed) onRemove(resource);
              }}
              onContextMenu={(event) => {
                if (!removeAllowed) return;
                event.preventDefault();
                onRemove(resource);
              }}
            >
              <ResourceBadge resource={resource} amount={amount} compact={compact} />
            </button>
            <button
              type="button"
              className="resource-count-step"
              disabled={!addAllowed}
              aria-label={`Eén meer ${meta.label}`}
              onClick={() => onAdd(resource)}
            >+</button>
          </div>
        );
      })}
      {selected.length ? (
        <button type="button" className="selection-clear" disabled={disabled} onClick={onClear}>
          {clearLabel}
        </button>
      ) : null}
    </div>
  );
}

function resourceShape(resource: Resource) {
  switch (resource) {
    case "hout":
      return (
        <>
          <path d="M12 2.2 6.7 8.8h3.1l-4.4 5.4h4L6.9 18h10.2l-2.5-3.8h4l-4.4-5.4h3.1Z" fill="currentColor" stroke="currentColor" strokeLinejoin="round" />
          <path d="M12 16.8v5" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="2.2" />
        </>
      );
    case "baksteen":
      return (
        <>
          <rect x="2.5" y="4.2" width="19" height="15.6" rx="1.5" fill="none" stroke="currentColor" strokeWidth="2" />
          <path d="M2.8 9.4h18.4M2.8 14.7h18.4M8.7 4.5v4.7m6.7-4.7v4.7M6 9.7v4.8m6-4.8v4.8m6-4.8v4.8M8.7 15v4.5m6.7-4.5v4.5" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.6" />
        </>
      );
    case "graan":
      return (
        <>
          <path d="M12 21.5V4" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="2" />
          <path d="M11.8 8.2C8.1 8 6.4 6.2 6.5 3.2c3.3.1 5.1 1.7 5.3 5Zm.4 3.8c3.7-.2 5.4-2 5.3-5-3.3.1-5.1 1.7-5.3 5Zm-.4 3.7c-3.7-.2-5.4-2-5.3-5 3.3.1 5.1 1.7 5.3 5Zm.4 3.7c3.7-.2 5.4-2 5.3-5-3.3.1-5.1 1.7-5.3 5Z" fill="currentColor" />
        </>
      );
    case "wol":
      return (
        <>
          <path d="M7.2 17.2a4 4 0 0 1-1.8-7.6A4.6 4.6 0 0 1 14 7.2a3.8 3.8 0 0 1 3.1 6.9 4.1 4.1 0 0 1-3.8 3.1Z" fill="currentColor" />
          <path d="M16.1 10.3c3.2-.8 5.4.8 5.4 3.3 0 2-1.6 3.3-3.8 3.3h-1.6m-8.5-.4-.8 4m6.6-4 .8 4m4.3-7.8 1-1.4" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
          <circle cx="19" cy="13.7" r=".8" fill="var(--resource-ink, #14221d)" />
        </>
      );
    case "erts":
      return (
        <>
          <path d="m2.8 18.8 3.8-8.5 5.1.7 3.2-6 6.3 10.9-4.5 4.2H6.1Z" fill="currentColor" stroke="currentColor" strokeLinejoin="round" />
          <path d="m6.6 10.3 3.1 4.2 2-3.5m3.2-6 1.8 8.3 4.5 2.6m-11.5-1.4-3.6 5.6m10.6-6.8-4.8 6.8" fill="none" stroke="var(--resource-ink, #14221d)" strokeLinejoin="round" strokeWidth="1.25" />
        </>
      );
    case "goud":
      return (
        <>
          <ellipse cx="12" cy="6" rx="7.8" ry="3.2" fill="currentColor" stroke="currentColor" strokeWidth="1.3" />
          <path d="M4.2 6v5.8C4.2 13.6 7.7 15 12 15s7.8-1.4 7.8-3.2V6M4.2 11.8v5.8c0 1.8 3.5 3.2 7.8 3.2s7.8-1.4 7.8-3.2v-5.8" fill="none" stroke="currentColor" strokeWidth="1.8" />
          <path d="m12 3.8.7 1.4 1.6.2-1.2 1.1.3 1.6L12 7.3l-1.4.8.3-1.6-1.2-1.1 1.6-.2Z" fill="var(--resource-ink, #14221d)" />
        </>
      );
  }
}
